import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Coffee, Search, Calendar, Tag, Loader2, RotateCcw, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

function fmtMuscat(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      timeZone: "Asia/Muscat",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return iso;
  }
}

function muscatDateKey(iso) {
  if (!iso) return "";
  try {
    // en-CA gives YYYY-MM-DD, honoring the timezone
    return new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Muscat" });
  } catch {
    return "";
  }
}

function fmtDuration(seconds) {
  if (seconds == null || isNaN(seconds)) return "—";
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

export default function DeskSessions() {
  const [nameQuery, setNameQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [category, setCategory] = useState("all");

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["desk-sessions"],
    queryFn: () => api.get("/admin/desk-sessions"),
  });

  const categories = useMemo(() => {
    const set = new Set();
    sessions.forEach((s) => s.category && set.add(s.category));
    return Array.from(set).sort();
  }, [sessions]);

  const filtered = useMemo(() => {
    const q = nameQuery.trim().toLowerCase();
    return sessions.filter((s) => {
      if (q) {
        const hay = `${s.name || ""} ${s.staff_id || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (category !== "all" && s.category !== category) return false;
      if (dateFilter && muscatDateKey(s.in_at) !== dateFilter) return false;
      return true;
    });
  }, [sessions, nameQuery, category, dateFilter]);

  const hasFilters = nameQuery || dateFilter || category !== "all";
  const reset = () => {
    setNameQuery("");
    setDateFilter("");
    setCategory("all");
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="desk-sessions-page">
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="desk-sessions-filters">
        <div className="flex items-center gap-2.5 mb-4">
          <span className="grid place-items-center h-9 w-9 rounded-lg bg-violet-50 text-violet-600">
            <Coffee className="h-4.5 w-4.5" />
          </span>
          <div>
            <h3 className="font-head text-base font-bold text-slate-900">Desk Session History</h3>
            <p className="text-xs text-slate-400 mt-0.5">Leave-desk activity logged from the mobile app · Asia / Muscat</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="desk-filter-name"
              value={nameQuery}
              onChange={(e) => setNameQuery(e.target.value)}
              placeholder="Search staff name or ID"
              className="h-11 w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
            />
          </div>

          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="desk-filter-date"
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="h-11 w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100"
            />
          </div>

          <div className="relative">
            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
            <select
              data-testid="desk-filter-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="h-11 w-full rounded-lg border border-slate-200 pl-9 pr-3 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 bg-white appearance-none"
            >
              <option value="all">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <button
            data-testid="desk-filter-reset"
            onClick={reset}
            disabled={!hasFilters}
            className="h-11 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-4 w-4" /> Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-700">Sessions</p>
          <span className="text-xs font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-3 py-1" data-testid="desk-sessions-count">
            {filtered.length} of {sessions.length}
          </span>
        </div>

        {isLoading ? (
          <div className="h-48 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>
        ) : filtered.length === 0 ? (
          <div className="h-48 grid place-items-center text-sm text-slate-400" data-testid="desk-sessions-empty">
            {sessions.length === 0 ? "No desk sessions recorded yet" : "No sessions match your filters"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="desk-sessions-table">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100">
                  <th className="px-5 py-3">Staff</th>
                  <th className="px-5 py-3">Reason</th>
                  <th className="px-5 py-3">Site</th>
                  <th className="px-5 py-3">Start</th>
                  <th className="px-5 py-3">End</th>
                  <th className="px-5 py-3">Duration</th>
                  <th className="px-5 py-3">Outcome</th>
                  <th className="px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50/60 transition-colors" data-testid={`desk-session-row-${s.id}`}>
                    <td className="px-5 py-3.5">
                      <div className="font-semibold text-slate-800">{s.name || "—"}</div>
                      <div className="text-xs text-slate-400">{s.staff_id}</div>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold px-2.5 py-1">
                        {s.category || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-600">{s.site_name || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-600 whitespace-nowrap">{fmtMuscat(s.in_at)}</td>
                    <td className="px-5 py-3.5 text-slate-600 whitespace-nowrap">{s.active ? "—" : fmtMuscat(s.out_at)}</td>
                    <td className="px-5 py-3.5 font-semibold text-slate-800 whitespace-nowrap">{fmtDuration(s.duration_seconds)}</td>
                    <td className="px-5 py-3.5">
                      {s.outcome === "success" ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1" data-testid={`desk-outcome-${s.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Success
                        </span>
                      ) : s.outcome === "unsuccess" ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 text-rose-700 text-xs font-semibold px-2.5 py-1" data-testid={`desk-outcome-${s.id}`}>
                          <XCircle className="h-3.5 w-3.5" /> Unsuccess
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400" data-testid={`desk-outcome-${s.id}`}>—</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      {s.active ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-500 text-xs font-semibold px-2.5 py-1">
                          Closed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
